import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  UserIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface UserDetail {
  user: {
    id: string;
    email: string;
    name: string;
    username: string;
    emailVerified: boolean;
    subscriptionPlan: string;
    subscriptionStatus: string;
    tokenBalance: number;
    isTestUser: boolean;
    isAdmin: boolean;
    isActive: boolean;
    createdAt: string;
    lastLoginAt: string;
  };
  stats: {
    totalSpent: number;
    totalTokensUsed: number;
    companionsCount: number;
    conversationsCount: number;
    paymentsCount: number;
  };
  companions: Array<{
    id: string;
    name: string;
    gender: string;
    createdAt: string;
  }>;
  recentActivity: Array<{
    id: string;
    action: string;
    details: any;
    createdAt: string;
  }>;
}

const AdminUserDetail: React.FC = () => {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();
  const { userId } = useParams();

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Admin access required');
        navigate('/login');
      } else {
        setError('Failed to load user details');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const toggleAdminStatus = async () => {
    if (!user) return;

    const confirmMessage = user.user.isAdmin
      ? 'Are you sure you want to remove admin privileges from this user?'
      : 'Are you sure you want to grant admin privileges to this user?';

    if (!window.confirm(confirmMessage)) return;

    try {
      setUpdating(true);
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `/admin/users/${userId}`,
        { isAdmin: !user.user.isAdmin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Admin status ${user.user.isAdmin ? 'removed' : 'granted'} successfully`);
      await fetchUserDetails(); // Refresh user data
    } catch (err: any) {
      toast.error('Failed to update admin status');
    } finally {
      setUpdating(false);
    }
  };

  const toggleUserStatus = async () => {
    if (!user) return;

    const confirmMessage = user.user.isActive
      ? 'Are you sure you want to deactivate this user?'
      : 'Are you sure you want to activate this user?';

    if (!window.confirm(confirmMessage)) return;

    try {
      setUpdating(true);
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `/admin/users/${userId}`,
        { isActive: !user.user.isActive },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`User ${user.user.isActive ? 'deactivated' : 'activated'} successfully`);
      await fetchUserDetails(); // Refresh user data
    } catch (err: any) {
      toast.error('Failed to update user status');
    } finally {
      setUpdating(false);
    }
  };

  const resetUserPassword = async () => {
    if (!user) return;

    if (!window.confirm('Are you sure you want to send a password reset email to this user?'))
      return;

    try {
      setUpdating(true);
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Password reset email sent successfully');
    } catch (err: any) {
      toast.error('Failed to send password reset email');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600">{error || 'User not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/users')}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
                <p className="text-sm text-gray-600 mt-1">{user.user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user.user.isAdmin && (
                <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                  Admin
                </span>
              )}
              {user.user.isTestUser && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                  Test User
                </span>
              )}
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  user.user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {user.user.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-gray-400" />
                Basic Information
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.user.name || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Username</dt>
                  <dd className="mt-1 text-sm text-gray-900">{user.user.username || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    {user.user.email}
                    {user.user.emailVerified && (
                      <span className="text-green-600">
                        <ShieldCheckIcon className="w-4 h-4" />
                      </span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">User ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">{user.user.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(user.user.createdAt).toLocaleDateString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Login</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {user.user.lastLoginAt
                      ? new Date(user.user.lastLoginAt).toLocaleString()
                      : 'Never'}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Subscription Info */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <CreditCardIcon className="w-5 h-5 text-gray-400" />
                Subscription & Tokens
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Plan</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">
                    {user.user.subscriptionPlan}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">
                    {user.user.subscriptionStatus}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Token Balance</dt>
                  <dd className="mt-1 text-sm text-gray-900">⭐ {user.user.tokenBalance}</dd>
                </div>
              </dl>
            </div>

            {/* Companions */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-gray-400" />
                Companions ({user.companions?.length || 0})
              </h2>
              {user.companions && user.companions.length > 0 ? (
                <div className="space-y-2">
                  {user.companions.map((companion) => (
                    <Link
                      key={companion.id}
                      to={`/admin/companions/${companion.id}`}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">{companion.name}</div>
                        <div className="text-xs text-gray-500">
                          Created {new Date(companion.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            companion.gender === 'female'
                              ? 'bg-pink-100 text-pink-800'
                              : companion.gender === 'male'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {companion.gender}
                        </span>
                        <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No companions created</p>
              )}
            </div>

            {/* Usage Stats */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-400" />
                Usage Statistics
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Total Companions:</span>
                  <span className="ml-2 font-medium">{user.stats.companionsCount}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Conversations:</span>
                  <span className="ml-2 font-medium">{user.stats.conversationsCount}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Spent:</span>
                  <span className="ml-2 font-medium">
                    ${(user.stats.totalSpent / 100).toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Tokens Used:</span>
                  <span className="ml-2 font-medium">
                    {user.stats.totalTokensUsed.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Payments Count:</span>
                  <span className="ml-2 font-medium">{user.stats.paymentsCount}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            {/* Admin Actions */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <ShieldCheckIcon className="w-5 h-5 text-gray-400" />
                Admin Actions
              </h2>
              <div className="space-y-3">
                <button
                  onClick={toggleAdminStatus}
                  disabled={updating}
                  className={`w-full px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    user.user.isAdmin
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  } disabled:opacity-50`}
                >
                  {updating ? 'Updating...' : user.user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                </button>
                <button
                  onClick={toggleUserStatus}
                  disabled={updating}
                  className={`w-full px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    user.user.isActive
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  } disabled:opacity-50`}
                >
                  {updating
                    ? 'Updating...'
                    : user.user.isActive
                      ? 'Deactivate User'
                      : 'Activate User'}
                </button>
                <button
                  onClick={resetUserPassword}
                  disabled={updating}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {updating ? 'Sending...' : 'Send Password Reset'}
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-gray-400" />
                Recent Activity
              </h2>
              {user.recentActivity && user.recentActivity.length > 0 ? (
                <div className="space-y-2">
                  {user.recentActivity.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="border-b border-gray-200 pb-2 last:border-0">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 capitalize">
                          {activity.action.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(activity.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No recent activity</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetail;
